import React from "react";
import { commitMutation, graphql } from "react-relay";
import { withRouter } from "react-router-dom";

import { faQuestionCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compact, groupBy, pick, unionBy } from "lodash";

import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { setTooltip } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toast from "~/ui/toast";

import type { FilterFunction } from "./ContactSearch";
import {
	type Contact,
	ContactSearchStep,
	type GenericInviteProps,
	InviteContact,
	type Relationship,
	type Step,
} from "./GenericInvite";

import type {
	OneOnOneInviteUsersMutation,
	OneOnOneInviteUsersMutation$data,
} from "~/__generated__/OneOnOneInviteUsersMutation.graphql";

import styles from "./css/GenericInvite.module.css";

const InviteUsersMutation = graphql`
	mutation OneOnOneInviteUsersMutation(
		$invite: InviteUsersMutationInput!
		$toggle: ToggleArchiveMultipleNoteStreamsInput!
	) {
		inviteUsers(input: $invite) {
			invitees {
				errors {
					field
					message
				}
				streamEdge {
					cursor
					node {
						id
						url
						otherUser {
							id
							email
							fullName
							image
							title
							department
							isActive
							isConfigured
							isGuest
							internalAttributes {
								manager {
									id
									fullName
								}
							}
						}
					}
				}
				user {
					id
					email
					fullName
					isConfigured
				}
			}
		}
		toggleArchiveMultiple(input: $toggle) {
			noteStreams {
				id
				url
				index
				archived
				starOrder
				starred
			}
		}
	}
`;

export type OneOnOneInvitees = NonNullable<OneOnOneInviteUsersMutation$data["inviteUsers"]>["invitees"];
export type OneOnOneToggleStreams = NonNullable<
	OneOnOneInviteUsersMutation$data["toggleArchiveMultiple"]
>["noteStreams"];

interface Props extends GenericInviteProps {
	filter?: FilterFunction;
	onComplete?: (args: { invitees?: OneOnOneInvitees; streams?: OneOnOneToggleStreams; inviteSent: boolean }) => void;
}

export default withRouter(function OneOnOneInvite({ onClose, onComplete, ...props }: Props) {
	const [step, setStep] = React.useState<Step>("contacts");
	const [contacts, setContacts] = React.useState<Contact[]>([]);
	const [inviting, setInviting] = React.useState(false);

	/* eslint-disable camelcase */
	const analytics = React.useMemo(
		() => ({
			tracking_category: "ux__web",
			invite_type: "one_on_one",
			...props.analytics,
		}),
		[props.analytics],
	);
	/* eslint-enable camelcase */

	const closeModal = React.useCallback(() => {
		onClose?.();

		setStep("contacts");
		setContacts([]);

		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Closed", analytics);
	}, [analytics, onClose]);

	React.useEffect(() => {
		window.hj?.("trigger", "invitedialog_opened");
		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Opened", analytics);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const displayError = React.useCallback((msg: string, { reset = false, autoClose = 5000 } = {}) => {
		toast.error(
			<div>
				<strong>Error:</strong> Something went wrong inviting your teammates.
				<br />
				<strong>{msg}</strong>
			</div>,
			{ autoClose },
		);
		if (reset) setContacts([]);
	}, []);

	const inviteMultipleContacts = React.useCallback(async () => {
		setInviting(true);

		const groups = groupBy(contacts, ({ user }) => {
			return user?.internalAttributes?.oneOnOneWithViewer?.id ? "notestreamIds" : "invitations";
		});

		const invitations = compact(
			groups["invitations"]?.map(contact => ({
				...pick(contact, ["email", "fullName", "relationship", "sendInviteEmail"]),
				oneOnOneDetails: {},
			})),
		);

		const captchaToken = await recaptcha("one_on_one_invite");
		const notestreamIds = compact(
			groups["notestreamIds"]?.map(({ user }) => user?.internalAttributes?.oneOnOneWithViewer?.id),
		);

		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Invited", { num_invites: invitations.length, ...analytics });

		commitMutation<OneOnOneInviteUsersMutation>(environment, {
			mutation: InviteUsersMutation,
			variables: {
				invite: {
					invitations,
					captchaToken,
					analytics: JSON.stringify(analytics),
				},
				toggle: {
					notestreamIds,
					value: false, // the user's intention is to always set the value to false
				},
			},
			onCompleted: ({ inviteUsers, toggleArchiveMultiple }, err) => {
				setInviting(false);

				if (err) return displayError(err[0].message);

				if (inviteUsers || toggleArchiveMultiple) {
					const invitees = inviteUsers?.invitees;
					const streams = toggleArchiveMultiple?.noteStreams;

					invitees?.forEach(({ errors }) => errors?.[0] && displayError(errors[0].message, { autoClose: undefined }));

					const successful = invitees?.filter(({ errors }) => !errors?.length) || [];
					const addedContacts = groupBy(successful, ({ user }) => {
						return user?.isConfigured ? "configured" : "invited";
					});

					if (addedContacts["invited"]?.length) {
						onComplete?.({ invitees: addedContacts["invited"], inviteSent: true, streams });
					} else if (addedContacts["configured"]?.length) {
						onComplete?.({ invitees: addedContacts["configured"], inviteSent: false, streams });
					} else if (streams?.length) {
						onComplete?.({ inviteSent: false, streams });
					}
				}

				closeModal();
			},
		});
	}, [contacts, closeModal, displayError, onComplete, setInviting, analytics]);

	const stepToRelationships = React.useCallback(() => {
		if (!contacts.some(({ user }) => !user || !user.isConfigured)) {
			inviteMultipleContacts();
			return;
		}

		if (!contacts.some(({ user }) => !user?.internalAttributes?.oneOnOneWithViewer?.id)) {
			inviteMultipleContacts();
			return;
		}

		setStep("relationships");
	}, [contacts, inviteMultipleContacts, setStep]);

	const backToContacts = React.useCallback(() => {
		setStep("contacts");
	}, []);

	const onRelationshipChange = React.useCallback(
		(contact: Contact, value: Relationship) => {
			contact.relationship = value;
			setContacts(unionBy([contact], contacts, "id"));
		},
		[contacts],
	);

	function renderContactSearch() {
		return (
			<ContactSearchStep
				excludeExternal
				contacts={contacts}
				inviting={inviting}
				analytics={analytics}
				setContacts={setContacts}
				onNext={stepToRelationships}
			/>
		);
	}

	function renderRelationships() {
		return (
			<div className={styles.contacts}>
				{contacts.map(contact => (
					<InviteContact key={contact.id} contact={contact} onRelationshipChange={onRelationshipChange} />
				))}
			</div>
		);
	}

	return (
		<Modal onClose={closeModal}>
			<Modal.Header>
				<Modal.Title>{step === "relationships" ? "Set your relationships" : "Add your teammates"}</Modal.Title>
				<Modal.HeaderActions onClose={closeModal}>
					<div
						className={styles.helpIcon}
						{...setTooltip(
							// eslint-disable-next-line max-len
							"Invite your manager, direct reports, mentor, coaches or other teammates to join your workspace on Fellow.",
							{
								inverted: true,
								position: "top center",
								width: "256px",
							},
						)}
					>
						<FontAwesomeIcon icon={faQuestionCircle} />
					</div>
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.inviteContainer} data-test-id="invite-container">
				<React.Suspense
					fallback={
						<div className={styles.loaderContainer}>
							<LoadingSpinner inline className={styles.loader} />
						</div>
					}
				>
					{contacts.length > 0 && step === "relationships" ? renderRelationships() : renderContactSearch()}
				</React.Suspense>
			</Modal.Body>
			{step === "relationships" && (
				<Modal.Footer>
					<Button size="normal" onClick={backToContacts} disabled={inviting}>
						Back
					</Button>
					<Button
						variant="primary"
						size="normal"
						onClick={inviteMultipleContacts}
						disabled={inviting}
						data-test-id="generic-invite-relationships-next"
					>
						{inviting ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : "Send invites"}
					</Button>
				</Modal.Footer>
			)}
		</Modal>
	);
});
